/**
 * ScratchCard — public-survey adapter around the design-authored canvas
 * (`_vendor/ScratchCardCanvas`, ported from the root `/games` library, the
 * visual source of truth).
 *
 * This file owns the app contract only: it picks the winner once via the
 * shared `pickPrize` (weighted by rarity), maps the live stock to the canvas
 * `WheelSegment`, resolves the 5-color brand from the runtime `primary`/
 * `accent`/`logo`, and bridges the canvas `onRevealed` back to the wizard's
 * `onWin`. All visuals/interaction live in the vendored canvas.
 */
import { useMemo } from 'react';
import type { GamePlayProps } from './pickPrize';
import { pickPrize } from './pickPrize';
import { ScratchCard as ScratchCardCanvas, type FoilLook, type RevealStyle } from './_vendor/ScratchCardCanvas';
import { resolveBrand } from './_shared/resolveBrand';
import { gameLang, toSegment } from './_shared/segments';
import { GameEmpty } from './_shared/GameEmpty';

export function ScratchCard({ items, primary, accent, onWin, lang, logo, config }: GamePlayProps) {
  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  const winnerIdx = useMemo(() => pickPrize(valid), [valid]);

  if (valid.length === 0 || winnerIdx < 0) return <GameEmpty />;

  const winner = valid[winnerIdx];
  const brand = resolveBrand({ primary, accent, logo });

  return (
    <div className="flex w-full justify-center">
      <ScratchCardCanvas
        prize={toSegment(winner)}
        brand={brand}
        language={gameLang(lang)}
        foil={(config?.foil as FoilLook | undefined) ?? 'metallic'}
        revealStyle={(config?.revealStyle as RevealStyle | undefined) ?? 'single'}
        onRevealed={() => onWin(winner)}
      />
    </div>
  );
}
